"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Loader2, RefreshCw, ShieldCheck } from "lucide-react";

import { AliyunCaptcha, type AliyunCaptchaHandle, type CaptchaVerifyOutcome } from "@/components/aliyun-captcha";
import { Button } from "@/components/ui/button";
import { securePost } from "@/lib/security/fetch-client";

const CAPTCHA_PREFIX = process.env.NEXT_PUBLIC_ALIYUN_CAPTCHA_PREFIX;
const CAPTCHA_SCENE_ID = process.env.NEXT_PUBLIC_ALIYUN_CAPTCHA_RISK_SCENE_ID;
const CAPTCHA_CONFIGURED = Boolean(CAPTCHA_PREFIX && CAPTCHA_SCENE_ID);

interface ChallengeResponse {
  ok?: boolean;
  next?: string;
  expiresAt?: number;
  error?: string;
  captchaVerifyResult?: boolean;
}

function safeNext(value: string | null): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/";
  if (value.startsWith("/api/") || value.startsWith("/security/challenge")) return "/";
  return value;
}

export function RiskChallengeClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const captchaRef = useRef<AliyunCaptchaHandle>(null);
  const autoTriggerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "verifying" | "passed" | "error">("loading");
  const [error, setError] = useState("");
  const next = useMemo(() => safeNext(searchParams.get("next")), [searchParams]);

  useEffect(() => {
    return () => {
      if (autoTriggerTimerRef.current) clearTimeout(autoTriggerTimerRef.current);
    };
  }, []);

  const verify = useCallback(async (captchaVerifyParam: string): Promise<CaptchaVerifyOutcome> => {
    setStatus("verifying");
    setError("");
    try {
      const result = await securePost<ChallengeResponse>("/api/security/challenge", {
        captchaVerifyParam,
        next,
      });
      if (!result.ok) {
        setStatus("error");
        setError(result.error || "验证失败，请重试");
        return { captchaResult: result.captchaVerifyResult !== false, bizResult: false, payload: result };
      }
      setStatus("passed");
      return { captchaResult: true, bizResult: true, payload: result };
    } catch {
      setStatus("error");
      setError("验证服务暂不可用，请稍后重试");
      return { captchaResult: true, bizResult: false };
    }
  }, [next]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <section className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-slate-950">安全验证</h1>
            <p className="mt-1 text-sm text-slate-500">完成后将继续访问当前页面。</p>
          </div>
        </div>

        <div className="mt-6 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {status === "loading" && <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />正在加载验证组件</span>}
          {status === "ready" && "验证组件已就绪，正在启动"}
          {status === "verifying" && <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />正在验证</span>}
          {status === "passed" && "验证通过，正在跳转"}
          {status === "error" && error}
          {!CAPTCHA_CONFIGURED && "验证码未配置"}
        </div>

        {CAPTCHA_CONFIGURED ? (
          <AliyunCaptcha
            ref={captchaRef}
            sceneId={CAPTCHA_SCENE_ID!}
            prefix={CAPTCHA_PREFIX!}
            onVerify={verify}
            onBizSuccess={(payload) => {
              const target = (payload as ChallengeResponse | undefined)?.next || next;
              router.replace(target);
            }}
            onBizError={(payload) => {
              const message = (payload as ChallengeResponse | undefined)?.error;
              setStatus("error");
              setError(message || "验证失败，请重试");
            }}
            onReady={() => {
              setStatus("ready");
              if (autoTriggerTimerRef.current) clearTimeout(autoTriggerTimerRef.current);
              autoTriggerTimerRef.current = setTimeout(() => captchaRef.current?.trigger(), 2500);
            }}
            onError={(message) => {
              setStatus("error");
              setError(message);
            }}
          />
        ) : null}

        <Button
          type="button"
          className="mt-5 w-full"
          onClick={() => captchaRef.current?.trigger()}
          disabled={!CAPTCHA_CONFIGURED || status === "loading" || status === "verifying" || status === "passed"}
        >
          {status === "verifying" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          重新验证
        </Button>
      </section>
    </main>
  );
}
